import moment from 'moment';
import { DS_F2W_Edit, DS_F2W_HR_Team, DS_F2W_Business_Travel_Team } from 'Constants/F2wRolesConstants';

/**
 * Hook that encapsulates the disabled-state and date-validation logic
 * for the BusinessSupportCard form fields.
 *
 * @param {string[]|undefined} userRoles - Current user's roles
 * @param {object} flyToWorkTicketInfo - The ticket info object
 * @returns {object} Computed disabled states and validation helpers
 */
const useBusinessSupportForm = (userRoles, flyToWorkTicketInfo) => {
  const status = flyToWorkTicketInfo?.status?.toLowerCase();
  const direction = flyToWorkTicketInfo?.direction?.toLowerCase();
  const typeOfFlight = flyToWorkTicketInfo?.typeOfFlight?.toLowerCase();

  /**
   * Validates whether a given date is selectable in the preferred-flight-date picker.
   * Restricts the window to ±3 days around the planned assignment start date
   * for Start-of-Season (arriving) and a wider window for End-of-Season (departure).
   */
  const isPreferredFlightDateValid = (currentDate) => {
    if (!currentDate || !moment.isMoment(currentDate) || !currentDate.isValid()) {
      return true;
    }

    const isSOS = userRoles?.includes(DS_F2W_Edit) && direction === 'arriving' && typeOfFlight === 'start of season';

    const isEOS = userRoles?.includes(DS_F2W_Edit) && direction === 'departure' && typeOfFlight === 'end of season';

    if (isSOS && flyToWorkTicketInfo.plannedAssignmentStartDate) {
      const planned = moment.utc(flyToWorkTicketInfo.plannedAssignmentStartDate).startOf('day');
      if (planned.isBefore(moment.utc().startOf('day'))) {
        return true;
      }
      const minDate = planned.clone().subtract(3, 'days');
      const maxDate = planned.clone().add(3, 'days');
      return currentDate.isBetween(minDate, maxDate, 'day', '[]');
    }

    if (isEOS && flyToWorkTicketInfo.plannedAssignmentStartDate) {
      const planned = moment.utc(flyToWorkTicketInfo.plannedAssignmentStartDate).startOf('day');
      if (planned.isBefore(moment.utc().startOf('day'))) {
        return true;
      }
      const minDate = planned.clone().subtract(1, 'year');
      const maxDate = planned.clone().add(3, 'days');
      return currentDate.isBetween(minDate, maxDate, 'day', '[]');
    }

    return true;
  };

  /** Preferred Flight Date is editable only for specific role+status combos. */
  const isPreferredFlightDateDisabled = () => {
    if (userRoles?.includes(DS_F2W_HR_Team) && flyToWorkTicketInfo?.noFlightNeeded && status === 'pendinghr')
      return false;

    if (userRoles?.includes(DS_F2W_Edit) && (status === 'pendingdes' || status === 'new')) return false;

    return true;
  };

  /** Arrival / Departure airport selects are disabled unless the user has the right role+status. */
  const isArrivalDepartureAirportDisabled = () => {
    if (flyToWorkTicketInfo?.noFlightNeeded) return true;

    if (userRoles?.includes(DS_F2W_Edit) && (status === 'pendingdes' || status === 'confirmed' || status === 'new'))
      return false;

    return true;
  };

  /** OS (operational support) fields are disabled unless the user has the right role+status. */
  const isOSFieldDisabled = () => {
    if (
      userRoles?.includes(DS_F2W_Business_Travel_Team) &&
      (status === 'pendingdes' || status === 'confirmed' || status === 'pendingbtt')
    )
      return false;

    return !(userRoles?.includes(DS_F2W_Edit) && (status === 'pendingdes' || status === 'new'));
  };

  /** OS fields disabled state when "no travel needed" is checked. */
  const isOSFieldDisabledNoTravelNeeded = () => {
    return !(
      userRoles?.includes(DS_F2W_Edit) ||
      (userRoles?.includes(DS_F2W_Business_Travel_Team) &&
        (status === 'pendingdes' || status === 'confirmed' || status === 'new'))
    );
  };

  /** GreenLight checkbox is only editable by HR team when status is pendinghr. */
  const isGreenLightCheckBoxDisabled = () => {
    return !(userRoles?.includes(DS_F2W_HR_Team) && status === 'pendinghr');
  };

  return {
    isPreferredFlightDateValid,
    isPreferredFlightDateDisabled,
    isArrivalDepartureAirportDisabled,
    isOSFieldDisabled,
    isOSFieldDisabledNoTravelNeeded,
    isGreenLightCheckBoxDisabled,
  };
};

export default useBusinessSupportForm;
